import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { usePageTransition } from '../../utils/pageTransitions';
import Header from '../../components/ui/Header';
import Footer from '../../components/ui/Footer';
import Icon from '../../components/AppIcon';

const TermsOfService = () => {
    const pageTransition = usePageTransition();

    return (
        <>
            <div className="min-h-screen bg-[var(--color-background)]">
                <Header />
                <motion.div {...pageTransition} className="bg-slate-50 dark:bg-slate-900">
                    <Helmet>
                        <title>Conditions Générales d'Utilisation - ORAZEK Investment</title>
                        <meta name="description" content="Conditions générales d'utilisation des services ORAZEK Investment. Consultez nos termes et conditions." />
                        <meta name="robots" content="index, follow" />
                    </Helmet>

                    {/* Hero Section */}
                    <div className="bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white py-20">
                        <div className="max-w-4xl mx-auto px-6">
                            <div className="flex items-center mb-6">
                                <Icon name="FileText" size={48} className="text-blue-400" />
                            </div>
                            <h1 className="text-4xl md:text-5xl font-bold mb-4">Conditions Générales d'Utilisation</h1>
                            <p className="text-xl text-blue-200">Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="max-w-4xl mx-auto px-6 py-16">
                        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-8 md:p-12 space-y-8">

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Info" size={24} className="mr-3 text-blue-600" />
                                    1. Objet
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation du site web d'ORAZEK Investment ainsi que de l'ensemble de nos services. En accédant à ce site, vous acceptez sans réserve les présentes CGU.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Building2" size={24} className="mr-3 text-blue-600" />
                                    2. Présentation de la Société
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    ORAZEK Investment est une société de services basée en République Démocratique du Congo, spécialisée dans les domaines suivants :
                                </p>
                                <ul className="mt-4 space-y-2 text-slate-700 dark:text-slate-300">
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Solutions IT et infrastructure réseau</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Immobilier commercial et résidentiel</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Location de véhicules avec chauffeurs</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Conseil juridique et accompagnement d'entreprises</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Fournitures de bureau et événementiel</span>
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Users" size={24} className="mr-3 text-blue-600" />
                                    3. Accès au Site
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    Le site est accessible gratuitement à tout utilisateur disposant d'un accès à Internet. Tous les frais liés à l'accès au site, que ce soient les frais matériels, logiciels ou d'accès à Internet, sont exclusivement à la charge de l'utilisateur.
                                </p>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    ORAZEK Investment se réserve le droit de suspendre, modifier ou interrompre temporairement ou de façon permanente tout ou partie du site sans préavis, notamment pour des raisons de maintenance.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Briefcase" size={24} className="mr-3 text-blue-600" />
                                    4. Services Proposés
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    Le site permet aux utilisateurs de :
                                </p>
                                <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                                    <li className="flex items-start">
                                        <Icon name="ArrowRight" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Consulter notre catalogue de services et solutions</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="ArrowRight" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Demander des devis et renseignements</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="ArrowRight" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Consulter les biens immobiliers disponibles</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="ArrowRight" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Réserver des véhicules avec ou sans chauffeur</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="ArrowRight" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Nous contacter pour des prestations personnalisées</span>
                                    </li>
                                </ul>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mt-4">
                                    Les informations fournies sur le site sont données à titre indicatif. ORAZEK Investment s'efforce d'assurer l'exactitude et la mise à jour des informations, mais ne saurait être tenu responsable des erreurs ou omissions.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="UserCheck" size={24} className="mr-3 text-blue-600" />
                                    5. Obligations de l'Utilisateur
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    L'utilisateur s'engage à :
                                </p>
                                <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Fournir des informations exactes lors de toute demande de service</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Ne pas utiliser le site à des fins illégales ou frauduleuses</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Respecter les droits de propriété intellectuelle d'ORAZEK Investment</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="CheckCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Ne pas tenter de nuire au bon fonctionnement du site</span>
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Copyright" size={24} className="mr-3 text-blue-600" />
                                    6. Propriété Intellectuelle
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    L'ensemble des contenus présents sur ce site (textes, images, logos, graphismes, etc.) est la propriété exclusive d'ORAZEK Investment ou fait l'objet d'une autorisation d'utilisation. Toute reproduction, représentation, modification ou exploitation, totale ou partielle, sans autorisation préalable écrite est strictement interdite.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="CreditCard" size={24} className="mr-3 text-blue-600" />
                                    7. Tarifs et Paiement
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    Les tarifs de nos services sont indiqués en Francs Congolais (CDF) et/ou en Dollars Américains (USD), toutes taxes comprises. Nous nous réservons le droit de modifier nos tarifs à tout moment, mais les services seront facturés sur la base des tarifs en vigueur au moment de la commande.
                                </p>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Le paiement s'effectue selon les modalités convenues lors de la commande. Tout retard de paiement pourra entraîner la suspension des services.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="AlertTriangle" size={24} className="mr-3 text-blue-600" />
                                    8. Limitation de Responsabilité
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed mb-4">
                                    ORAZEK Investment ne saurait être tenu responsable :
                                </p>
                                <ul className="space-y-3 text-slate-700 dark:text-slate-300">
                                    <li className="flex items-start">
                                        <Icon name="XCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Des dommages directs ou indirects résultant de l'utilisation du site</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="XCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Des interruptions ou dysfonctionnements techniques</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="XCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>De l'impossibilité momentanée d'accès au site</span>
                                    </li>
                                    <li className="flex items-start">
                                        <Icon name="XCircle" size={20} className="mr-3 mt-0.5 text-blue-600 flex-shrink-0" />
                                        <span>Des virus ou logiciels malveillants qui pourraient infecter l'équipement de l'utilisateur</span>
                                    </li>
                                </ul>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Scale" size={24} className="mr-3 text-blue-600" />
                                    9. Droit Applicable et Juridiction
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Les présentes CGU sont régies par le droit congolais. En cas de litige, une solution amiable sera recherchée avant toute action judiciaire. À défaut, les tribunaux de Kinshasa seront seuls compétents.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Edit" size={24} className="mr-3 text-blue-600" />
                                    10. Modification des CGU
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    ORAZEK Investment se réserve le droit de modifier les présentes CGU à tout moment. Les nouvelles conditions prendront effet dès leur mise en ligne. Il est conseillé à l'utilisateur de consulter régulièrement cette page.
                                </p>
                            </section>

                            <section>
                                <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 flex items-center">
                                    <Icon name="Mail" size={24} className="mr-3 text-blue-600" />
                                    11. Contact
                                </h2>
                                <p className="text-slate-700 dark:text-slate-300 leading-relaxed">
                                    Pour toute question concernant ces CGU, vous pouvez nous contacter :
                                </p>
                                <div className="mt-4 p-6 bg-blue-50 dark:bg-blue-900/30 rounded-lg">
                                    <p className="text-slate-900 dark:text-white font-medium">ORAZEK Investment</p>
                                    <p className="text-slate-700 dark:text-slate-300">Kinshasa, République Démocratique du Congo</p>
                                    <p className="text-slate-700 dark:text-slate-300">Email : <a href="mailto:orazekinvestment5@gmail.com" className="text-blue-600 hover:underline">orazekinvestment5@gmail.com</a></p>
                                    <p className="text-slate-700 dark:text-slate-300">Téléphone : <a href="tel:+243818883531" className="text-blue-600 hover:underline">+243 81 888 3531</a></p>
                                </div>
                            </section>

                        </div>
                    </div>
                </motion.div>
                <Footer />
            </div>
        </>
    );
};

export default TermsOfService;

